import { z } from 'zod';
import { compteCourant } from '@/shared/donnees/demo/magasin';
import { enModeDemo, supabase } from '@/shared/supabase/client';

/**
 * Les incidents : ce qui a planté chez quelqu'un, écrit dans la base pour que
 * le bureau le voie sans attendre qu'on le lui raconte. Pas de service externe.
 *
 * Règle d'or : signaler ne lève jamais d'erreur. Un incident qui échoue à
 * s'enregistrer ne doit pas en créer un autre, ni casser l'écran qui l'envoie.
 */
export interface Incident {
  id: number;
  survenuLe: string;
  compteId: string | null;
  compteNom: string | null;
  role: string | null;
  page: string | null;
  message: string;
  detail: string | null;
  navigateur: string | null;
  version: string | null;
}

export interface Signalement {
  message: string;
  detail?: string | undefined;
  page: string;
}

/* Les plafonds de la base (db/012-incidents.sql) : on coupe avant d'envoyer,
   sans quoi la ligne serait refusée en entier. */
const LIMITES = { message: 2000, detail: 8000, page: 500, navigateur: 500, version: 100 };

export const VERSION = import.meta.env.VITE_VERSION ?? 'dev';

/** En accès libre : les 50 derniers, 20 fois au plus le même message par heure. */
export const CLE_DEMO = 'vla-demo-incidents';
export const MAX_DEMO = 50;
export const MAX_PAR_HEURE = 20;

function couper(texte: string, max: number): string {
  return texte.length > max ? texte.slice(0, max - 1) + '…' : texte;
}

function nettoyer(signalement: Signalement) {
  const message = signalement.message.trim() || 'Erreur sans message';
  const detail = signalement.detail?.trim();
  return {
    message: couper(message, LIMITES.message),
    detail: detail ? couper(detail, LIMITES.detail) : null,
    page: couper(signalement.page, LIMITES.page),
    navigateur: couper(navigator.userAgent, LIMITES.navigateur),
    version: couper(VERSION, LIMITES.version),
  };
}

/**
 * Ajoute un incident à la liste de l'accès libre (la plus récente d'abord), en
 * appliquant les mêmes garde-fous que la base : au-delà de `MAX_PAR_HEURE` fois
 * le même message pour le même compte dans l'heure, on ignore ; et on ne garde
 * que les `MAX_DEMO` plus récents.
 */
export function ajouterIncident(
  liste: readonly Incident[],
  nouveau: Omit<Incident, 'id'>,
  maintenant: Date
): Incident[] {
  const ilYaUneHeure = maintenant.getTime() - 3_600_000;
  const recents = liste.filter(
    (i) =>
      i.compteId === nouveau.compteId &&
      i.message === nouveau.message &&
      new Date(i.survenuLe).getTime() > ilYaUneHeure
  ).length;
  if (recents >= MAX_PAR_HEURE) return [...liste];
  const id = liste.reduce((max, i) => Math.max(max, i.id), 0) + 1;
  return [{ ...nouveau, id }, ...liste].slice(0, MAX_DEMO);
}

const incidentDemo = z.object({
  id: z.number(),
  survenuLe: z.string(),
  compteId: z.string().nullable(),
  compteNom: z.string().nullable(),
  role: z.string().nullable(),
  page: z.string().nullable(),
  message: z.string(),
  detail: z.string().nullable(),
  navigateur: z.string().nullable(),
  version: z.string().nullable(),
});

function lireDemo(): Incident[] {
  try {
    const brut = localStorage.getItem(CLE_DEMO);
    if (!brut) return [];
    const lecture = z.array(incidentDemo).safeParse(JSON.parse(brut));
    return lecture.success ? lecture.data : [];
  } catch {
    return [];
  }
}

function ecrireDemo(liste: Incident[]): void {
  try {
    localStorage.setItem(CLE_DEMO, JSON.stringify(liste));
  } catch {
    /* Stockage plein ou interdit : l'incident est perdu, pas l'écran. */
  }
}

/* Un incident survenu pendant l'envoi d'un autre ne repart pas en boucle. */
let enCours = false;

/** Enregistre un incident. Ne lève jamais, ne fait jamais attendre l'appelant. */
export function signalerIncident(signalement: Signalement): void {
  if (enCours) return;
  enCours = true;
  try {
    const ligne = nettoyer(signalement);
    if (enModeDemo()) {
      const compte = compteCourant();
      ecrireDemo(
        ajouterIncident(
          lireDemo(),
          {
            ...ligne,
            survenuLe: new Date().toISOString(),
            compteId: compte?.id ?? null,
            compteNom: compte?.nom ?? null,
            role: compte?.role ?? null,
          },
          new Date()
        )
      );
      enCours = false;
      return;
    }
    void envoyer(ligne)
      .catch(() => undefined)
      .finally(() => {
        enCours = false;
      });
  } catch {
    enCours = false;
  }
}

async function envoyer(ligne: ReturnType<typeof nettoyer>): Promise<void> {
  const client = supabase();
  /* Sans session, la base refuserait : inutile de faire le voyage. */
  const { data } = await client.auth.getSession();
  if (!data.session) return;
  /* `compte_id` et `role` sont posés par la base. */
  await client.from('incidents').insert(ligne);
}

const ligneIncident = z.object({
  id: z.number(),
  survenu_le: z.string(),
  compte_id: z.string().nullable(),
  role: z.string().nullable(),
  page: z.string().nullable(),
  message: z.string(),
  detail: z.string().nullable(),
  navigateur: z.string().nullable(),
  version: z.string().nullable(),
});

/** Les incidents les plus récents d'abord. Réservé au bureau. */
export const TAILLE_LISTE = 500;

export async function listerIncidents(): Promise<Incident[]> {
  if (enModeDemo()) return lireDemo();

  const { data, error } = await supabase()
    .from('incidents')
    .select('id, survenu_le, compte_id, role, page, message, detail, navigateur, version')
    .order('survenu_le', { ascending: false })
    .limit(TAILLE_LISTE);
  if (error) throw new Error('Les incidents n’ont pas pu être lus.');
  const lignes = z.array(ligneIncident).parse(data);

  const ids = [...new Set(lignes.flatMap((l) => (l.compte_id ? [l.compte_id] : [])))];
  const noms = new Map<string, string>();
  if (ids.length > 0) {
    const profils = await supabase().from('profils').select('id, nom, email').in('id', ids);
    if (!profils.error) {
      z.array(z.object({ id: z.string(), nom: z.string(), email: z.string() }))
        .parse(profils.data)
        .forEach((p) => noms.set(p.id, p.nom || p.email));
    }
  }

  return lignes.map((l) => ({
    id: l.id,
    survenuLe: l.survenu_le,
    compteId: l.compte_id,
    compteNom: l.compte_id ? (noms.get(l.compte_id) ?? null) : null,
    role: l.role,
    page: l.page,
    message: l.message,
    detail: l.detail,
    navigateur: l.navigateur,
    version: l.version,
  }));
}

/** Efface les incidents traités. Réservé au bureau. */
export async function supprimerIncidents(ids: readonly number[]): Promise<void> {
  if (ids.length === 0) return;
  if (enModeDemo()) {
    const aEffacer = new Set(ids);
    ecrireDemo(lireDemo().filter((i) => !aEffacer.has(i.id)));
    return;
  }
  /* Par paquets : la liste part dans l'adresse de la requête. */
  for (let debut = 0; debut < ids.length; debut += 200) {
    const { error } = await supabase()
      .from('incidents')
      .delete()
      .in('id', ids.slice(debut, debut + 200));
    if (error) throw new Error('Les incidents n’ont pas pu être effacés.');
  }
}

/* Ce qui n'est pas une panne de l'application : le réseau qui tombe, une
   extension du navigateur, une erreur d'un autre site sans détail, un
   déploiement survenu pendant que l'onglet était ouvert. */
const BRUIT =
  /Failed to fetch|NetworkError|Load failed|network error|fetch failed|ERR_NETWORK|ERR_INTERNET_DISCONNECTED|The Internet connection appears to be offline|AbortError|The user aborted a request|ResizeObserver loop|dynamically imported module|Importing a module script failed|^Script error\.?$/i;
const EXTENSION = /(chrome|moz|safari(-web)?|ms-browser)-extension:\/\//i;

/** Vrai pour une erreur qu'il ne sert à rien d'enregistrer. */
export function estDuBruit(message: string, pile = '', fichier = ''): boolean {
  return BRUIT.test(message) || EXTENSION.test(pile) || EXTENSION.test(fichier);
}

function decrire(raison: unknown): { message: string; pile: string } {
  if (raison instanceof Error) return { message: raison.message, pile: raison.stack ?? '' };
  if (typeof raison === 'string') return { message: raison, pile: '' };
  try {
    return { message: JSON.stringify(raison) || String(raison), pile: '' };
  } catch {
    return { message: 'Erreur illisible', pile: '' };
  }
}

let installe = false;

/** Écoute les erreurs que personne n'a rattrapées. Une seule installation. */
export function installerSignalement(): void {
  if (installe || typeof window === 'undefined') return;
  installe = true;

  window.addEventListener('error', (evenement) => {
    const { message, pile } = decrire(evenement.error ?? evenement.message);
    if (estDuBruit(message, pile, evenement.filename)) return;
    const ou = evenement.filename
      ? `${evenement.filename}:${evenement.lineno}:${evenement.colno}`
      : '';
    signalerIncident({
      message,
      detail: [pile, ou].filter(Boolean).join('\n\n'),
      page: window.location.pathname,
    });
  });

  window.addEventListener('unhandledrejection', (evenement) => {
    const { message, pile } = decrire(evenement.reason);
    if (estDuBruit(message, pile)) return;
    signalerIncident({ message, detail: pile, page: window.location.pathname });
  });
}
